import React, { useMemo } from "react"

import { useNavigate, useSearchParams } from "react-router-dom"

import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"

import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { SpinnerElement } from "@front-shared/elements/spinner.element"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook"

import { GENERAL_CONFIG } from "../../config/general.config"
import { ROUTES_CONFIG } from "../../config/routes.config"
import { InsightsHelper } from "../../helpers/insights.helper"
import { useHasPermissions } from "../../hooks/hasPermissions.hook"
import { useDemoMode } from "../../hooks/useDemoMode.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { DashboardEmbedComponent } from "./dashboardEmbed.component"

import arrowLeftIcon from "@front-shared/assets/images/icons/arrow-left-dark.svg"

export const InsightsComponent: React.FunctionComponent = () => {
    useScrollFix()

    const navigate = useNavigate()

    useHasPermissions(OneVerifyPermission.viewPracticeInsights)

    const { isDemo } = useDemoMode()
    const runtimeState = useAppSelector(runtimeSelector)

    const [searchParams] = useSearchParams()

    const embed = useMemo(() => {
        return InsightsHelper.getEmbedFromSearchParams(searchParams, isDemo)
    }, [searchParams, isDemo])

    const fromPath = useMemo(() => {
        return InsightsHelper.getSafeFromPath(searchParams)
    }, [searchParams])

    const backLabel = useMemo(() => {
        return InsightsHelper.getBackLabel(fromPath)
    }, [fromPath])

    const onGoBack = () => {
        if (fromPath) {
            navigate(fromPath)

            return
        }

        navigate(ROUTES_CONFIG.insightsUrl)
    }

    return runtimeState.enabledFeatureFlags ? (
        <div
            data-produktly="insights-page"
            className="relative flex flex-col"
            style={{
                height: `calc(100vh - ${GENERAL_CONFIG.headerHeight}px)`,
            }}
        >
            <PageHelmetElement
                title="Dashboard"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            {embed.isCustomEmbed ? (
                <div
                    className="
                        flex items-center px-[8px] py-[16px]
                        border-b border-solid border-brand-warmLight
                    "
                >
                    <div
                        className="p-[8px] mr-[8px] cursor-pointer"
                        onClick={onGoBack}
                    >
                        <img src={arrowLeftIcon} alt={backLabel} />
                    </div>

                    <PrimaryText typography="h3">{backLabel}</PrimaryText>
                </div>
            ) : null}

            <div
                data-produktly="insights-dashboard"
                className={`
                    min-h-0 flex-1
                    ${embed.isCustomEmbed ? "" : "mt-[24px]"}
                `}
            >
                <DashboardEmbedComponent
                    key={`${embed.embedType}-${embed.embedId}`}
                    style={{
                        height: "100%",
                    }}
                    embedType={embed.embedType}
                    embedId={embed.embedId}
                />
            </div>
        </div>
    ) : (
        <SpinnerElement containerClassName="text-center mt-[48px]" />
    )
}
